<template>
  <div class="user-page">
    <div class="uk-grid-medium" uk-grid>
        <div class="section-header pb-0 mt-2 uk-width-4-4@m">
          <div class="section-header-left">
            <h3 class="uk-text-truncate"><i class="uil-book-open"></i> {{ providerTitle }}</h3>
          </div>
        </div>
        <div class="uk-width-4">
          <div class="section-header pb-4">
            <div class="section-header-left">
              <form onsubmit="return false;">
                <input v-model="searchQuery" :placeholder="$t('filters.search')" type="text" @input="isTyping = true">
              </form>
            </div>
          </div>

          <!-- Skeleton Loading -->
          <div v-if="isLoading" class="uk-child-width-1-2 uk-child-width-1-3@s uk-child-width-1-4@m course-card-grid uk-grid-match mt-2" uk-grid>
            <div v-for="n in 8" :key="'skeleton-' + n">
              <ProviderCardSkeleton />
            </div>
          </div>

          <!-- Content -->
          <template v-else>
            <!-- Empty State -->
            <div v-if="results.length === 0" class="empty-state">
              <div class="empty-state-icon">
                <i class="uil-folder-open"></i>
              </div>
              <h4>{{ $t('general.no_result_found') }}</h4>
            </div>

            <!-- Results -->
            <template v-else>
              <div class="uk-child-width-1-2 uk-child-width-1-3@s uk-child-width-1-4@m course-card-grid uk-grid-match mt-2" uk-grid>
                <div v-for="(provider_item, index) in results" :key="'provider_item_' + index">
                  <ProviderCard :provider-item="provider_item" @click="openProviderDetails" />
                </div>
              </div>

              <!-- Load More Button -->
              <div v-if="results.length > 0" class="uk-flex uk-flex-center p-4">
                <button v-if="hasMore"
                        :disabled="loadingMore"
                        class="btn btn-info button_more"
                        @click.prevent="loadMore">
                  <span v-if="loadingMore" class="loading-dots">
                    <span></span>
                    <span></span>
                    <span></span>
                  </span>
                  <span v-else>{{ $t('general.more') }}</span>
                </button>
              </div>
            </template>
          </template>
        </div>
    </div>
  </div>
</template>

<script>
import { GET_ITEMS } from "@/core/services/store/REST.module";
import debounce from "lodash/debounce";
import axios from "axios";
import { getSubCompanyId } from "@/core/services/store.js";
import ProviderCard from "./components/ProviderCard.vue";
import ProviderCardSkeleton from "./components/ProviderCardSkeleton.vue";

const PER_PAGE = 20;

export default {
  name: "ProviderResults",
  components: {
    ProviderCard,
    ProviderCardSkeleton
  },
  data() {
    return {
      isLoading: true,
      loadingMore: false,
      restUrl: 'api/provider-search',
      searchQuery: "",
      isTyping: false,
      results: [],
      pageNumber: 1,
      totalItems: 0,
      lastPage: 1,
      lastFetchedCount: 0
    };
  },
  computed: {
    providerTitle() {
      const provider = this.$route.params.provider;
      if (!provider) return this.$t('general.list');

      const providerNames = {
        'aeroclass': 'Aeroclass',
        'linkedin': 'LinkedIn',
        'harvard': 'Harvard',
        'udemy': 'Udemy',
        'coursera': 'Coursera'
      };

      return providerNames[provider.toLowerCase()] || provider.charAt(0).toUpperCase() + provider.slice(1);
    },
    hasMore() {
      // last_page varsa ona göre, yoksa son fetch edilen veri sayısına göre karar ver
      if (this.lastPage > 1) {
        return this.pageNumber < this.lastPage;
      }
      // Eğer son fetch'te PER_PAGE kadar veri geldiyse, daha fazla olabilir
      return this.lastFetchedCount >= PER_PAGE;
    }
  },
  methods: {
    async loadMore() {
      if (this.loadingMore || !this.hasMore) return;

      this.loadingMore = true;
      this.pageNumber++;

      const filters = {
        page: this.pageNumber,
        limit: PER_PAGE,
        q: this.searchQuery,
        ...(this.$route.params.provider && { provider: this.$route.params.provider })
      };

      try {
        const result = await this.$store.dispatch(GET_ITEMS, {
          url: this.restUrl,
          acceptPromise: false,
          filters
        });

        if (result.status && result.data?.data) {
          const newData = result.data.data || [];
          this.results = [...this.results, ...newData];
          this.lastPage = result.data.last_page || 1;
          this.lastFetchedCount = newData.length;
        }
      } finally {
        this.loadingMore = false;
      }
    },
    async applyFilter() {
      this.isLoading = true;
      this.pageNumber = 1;

      const filters = {
        page: this.pageNumber,
        limit: PER_PAGE,
        q: this.searchQuery,
        ...(this.$route.params.provider && { provider: this.$route.params.provider })
      };

      try {
        const result = await this.$store.dispatch(GET_ITEMS, {
          url: this.restUrl,
          acceptPromise: false,
          filters,
          showLoading: false
        });

        if (result.status && result.data) {
          const data = result.data.data || [];
          this.results = data;
          this.totalItems = result.data.total || 0;
          this.lastPage = result.data.last_page || 1;
          this.lastFetchedCount = data.length;
        }
      } finally {
        this.isLoading = false;
      }
    },
    openProviderDetails(result) {
      axios.get(process.env.VUE_APP_BACKEND_APP_URL + "/api/provider-content-show/" + result.provider + "/" + result.id, {
        headers: {
          "Content-Type": "application/json",
          Authorization: "Bearer " + this.secureLocalStorage("token"),
          Company: getSubCompanyId?.value,
        },
      }).then((response) => {
        if (response.data.status) {
          this.$router.push({ name: "user.assignment-detail", params: { id: response.data.assignment_id } });
        }
      });
    },
  },
  watch: {
    '$route.params.provider': {
      handler() {
        this.results = [];
        this.pageNumber = 1;
        this.applyFilter();
      }
    },
    searchQuery: debounce(function () {
      this.isTyping = false;
    }, 1000),
    isTyping(value) {
      if (!value) {
        this.applyFilter();
      }
    },
  },
  mounted() {
    this.applyFilter();
  }
}
</script>

<style scoped>
.button_more {
  border-radius: 50% !important;
  height: 70px;
  width: 70px;
  background-color: var(--backgroundColor) !important;
  display: flex;
  align-items: center;
  justify-content: center;
}

.button_more:disabled {
  opacity: 0.7;
  cursor: not-allowed;
}

.loading-dots {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
}

.loading-dots span {
  width: 6px;
  height: 6px;
  background-color: white;
  border-radius: 50%;
  animation: dotPulse 1.4s infinite ease-in-out both;
}

.loading-dots span:nth-child(1) {
  animation-delay: -0.32s;
}

.loading-dots span:nth-child(2) {
  animation-delay: -0.16s;
}

.loading-dots span:nth-child(3) {
  animation-delay: 0s;
}

@keyframes dotPulse {
  0%, 80%, 100% {
    transform: scale(0.6);
    opacity: 0.5;
  }
  40% {
    transform: scale(1);
    opacity: 1;
  }
}

.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 20px;
  text-align: center;
}

.empty-state-icon {
  font-size: 64px;
  color: #ccc;
  margin-bottom: 16px;
}

.empty-state h4 {
  color: #666;
  font-weight: 500;
}
</style>
